import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Navigation, Search, X } from "lucide-react";
import { GoogleMap, Marker } from "@react-google-maps/api";
// import RestImageCarousel from "../main-page/components/rest-image-carousel/RestImageCarousel.jsx";
import { useRestaurants } from "../../hooks/useRestaurants";
import { RestaurantCard } from "../../components/RestaurantCard";
import AnimatedLoader from "../../components/AnimatedLoader";
import { calculateRestaurantDistances } from "../../utils/firebaseFunctionsDistance";
import "../../utils/testDistanceAPI"; // Load test function
import MainPageCarousel from "../main-page/components/rest-image-carousel/MainPageCarousel.jsx";

const MapAndStores = ({ restID }) => {
  const navigate = useNavigate();
  const { data: restaurants = [] } = useRestaurants();
  const [userLocation, setUserLocation] = useState(() => {
    const saved = localStorage.getItem("user-location");
    return saved ? JSON.parse(saved) : null;
  });
  const [nearestStores, setNearestStores] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isUsingDefaultLocation, setIsUsingDefaultLocation] = useState(false);
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);

  // Function to request user location
  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(newLocation);
          localStorage.setItem("user-location", JSON.stringify(newLocation));
          setIsUsingDefaultLocation(false);
        },
        (error) => {
          console.log(
            "Location access denied, using Hyderabad as default:",
            error
          );

          // Show alert if permission was denied
          if (error.code === error.PERMISSION_DENIED) {
            alert(
              "Location access was denied. Please enable location permissions in your browser settings to see nearby restaurants."
            );
          }

          // Set Hyderabad as default location
          setUserLocation({
            lat: 17.385044,
            lng: 78.486671,
          });
          setIsUsingDefaultLocation(true);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      // If geolocation is not supported, use Hyderabad as default
      alert("Geolocation is not supported by your browser.");
      setUserLocation({
        lat: 17.385044,
        lng: 78.486671,
      });
      setIsUsingDefaultLocation(true);
    }
  };

  // Set default location on mount (don't auto-request permission)
  useEffect(() => {
    // Check if we already have permission
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            // If already granted, get location automatically
            requestLocation();
          } else {
            // Otherwise, use default location and show enable button
            setUserLocation({
              lat: 17.385044,
              lng: 78.486671,
            });
            setIsUsingDefaultLocation(true);
          }
        })
        .catch(() => {
          // Fallback if permissions API not supported
          setUserLocation({
            lat: 17.385044,
            lng: 78.486671,
          });
          setIsUsingDefaultLocation(true);
        });
    } else {
      // Fallback for browsers without permissions API
      setUserLocation({
        lat: 17.385044,
        lng: 78.486671,
      });
      setIsUsingDefaultLocation(true);
    }
  }, []);

  const [visibleCount, setVisibleCount] = useState(5);

  // Calculate nearest stores using Google Maps Distance Matrix API
  useEffect(() => {
    if (userLocation && restaurants.length > 0) {
      // Helper to calculate distance between two coordinates in meters (Haversine)
      const getDistanceMeters = (loc1, loc2) => {
        if (!loc1 || !loc2) return Infinity;
        const R = 6371e3; // metres
        const φ1 = (loc1.lat * Math.PI) / 180;
        const φ2 = (loc2.lat * Math.PI) / 180;
        const Δφ = ((loc2.lat - loc1.lat) * Math.PI) / 180;
        const Δλ = ((loc2.lng - loc1.lng) * Math.PI) / 180;

        const a =
          Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
      };

      const calculateDistances = async () => {
        const activeRestaurants = restaurants.filter(
          (r) => r.storeLat != null && r.storeLong != null && r.isHighway === true
        );

        // --- 1km Threshold Check ---
        const savedLocation = localStorage.getItem("last-calculated-location");
        const lastLoc = savedLocation ? JSON.parse(savedLocation) : null;
        const movedDistance = getDistanceMeters(userLocation, lastLoc);
        
        // If moved less than 1km and we already have some stores, and it's not a "View More" trigger, skip
        const isInitialLoad = nearestStores.length === 0;
        const isViewMoreTrigger = nearestStores.length > 0 && nearestStores.length < visibleCount;
        
        if (movedDistance < 1000 && !isInitialLoad && !isViewMoreTrigger) {
          console.log(`Skipping refetch: User only moved ${movedDistance.toFixed(0)}m (threshold is 1000m)`);
          return;
        }
        // ----------------------------

        // 1. First, calculate Haversine distance for ALL (cost: $0)
        const withHaversine = activeRestaurants.map(r => ({
          ...r,
          haversineDist: getDistanceMeters(userLocation, { lat: r.storeLat, lng: r.storeLong }) / 1000
        }));

        // 2. Sort by Haversine distance and filter by 300km
        const sortedNearby = withHaversine
          .sort((a, b) => a.haversineDist - b.haversineDist);

        // 3. Take only the current page (visibleCount)
        const batchToCalculate = sortedNearby.slice(0, visibleCount);
        
        // Find which ones in the current batch don't have accurate distances yet
        const needsUpdate = batchToCalculate.filter(r => {
          const existing = nearestStores.find(ns => ns.dbID === r.dbID);
          return !existing || !existing.distanceText || movedDistance >= 1000;
        });

        if (needsUpdate.length === 0 && nearestStores.length >= Math.min(visibleCount, sortedNearby.length)) {
          return; 
        }

        setIsCalculatingDistances(true);
        console.log(`Calculating distances (Moved: ${movedDistance.toFixed(0)}m)...`);

        try {
          const destinations = batchToCalculate.map((r) => ({
            lat: r.storeLat,
            lng: r.storeLong,
          }));

          const results = await calculateRestaurantDistances(
            userLocation,
            destinations
          );

          // Map results back
          const updatedBatch = batchToCalculate.map((restaurant, index) => {
            const result = results[index];
            if (result && result.status === "OK") {
              return {
                ...restaurant,
                distance: result.distance,
                distanceText: result.distanceText,
                duration: result.duration,
                durationText: result.durationText,
              };
            }
            return {
              ...restaurant,
              distance: restaurant.haversineDist,
              distanceText: `${restaurant.haversineDist.toFixed(1)} km`
            };
          });

          const finalStores = updatedBatch.filter(r => r.distance <= 300);

          setNearestStores(finalStores);
          localStorage.setItem("last-calculated-location", JSON.stringify(userLocation));
          setIsCalculatingDistances(false);
        } catch (error) {
          console.error("Error calculating distances:", error);
          setIsCalculatingDistances(false);
          setNearestStores(batchToCalculate.map(r => ({
            ...r,
            distance: r.haversineDist,
            distanceText: `${r.haversineDist.toFixed(1)} km`
          })));
        }
      };

      calculateDistances();
    }
  }, [restaurants.length, userLocation, visibleCount]);



  const handleMapClick = () => {
    navigate("/restaurant-map");
  };

  const handleStoreClick = (storeId) => {
    navigate(`/prebooking/${storeId}`);
  };

  console.log({ nearestStores });
  // Filter restaurants based on search query for dropdown
  const searchResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    // Filter active restaurants and only those on highway
    const allActiveRestaurants = restaurants.filter(
      (r) => r && r.isHighway === true
    );

    console.log(
      "Search query:",
      query,
      "Total active restaurants:",
      allActiveRestaurants.length
    );
    console.log("Total active restaurants:", allActiveRestaurants.length);

    // Search across multiple fields
    const filtered = allActiveRestaurants.filter((restaurant) => {
      const storeName = (restaurant.name || "").toLowerCase();
      const storeAddress = (restaurant.address || "").toLowerCase();
      const storeType = (restaurant.storeType || "").toLowerCase();

      const matches =
        storeName.includes(query) ||
        storeAddress.includes(query) ||
        storeType.includes(query);

      return matches;
    });

    console.log("Filtered results:", filtered);

    return filtered.slice(0, 8); // Limit to 8 results in dropdown
  }, [searchQuery, restaurants]);

  console.log({ searchResults });

  const clearSearch = () => {
    setSearchQuery("");
    // setIsSearchFocused(false);
    // setShowDropdown(false);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setShowDropdown(true);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(true);
  };

  const handleDropdownItemClick = (storeId) => {
    navigate(`/prebooking/${storeId}`);
    setShowDropdown(false);
    setSearchQuery("");
  };

  // Calculate map center for preview
  const mapCenter = useMemo(() => {
    if (userLocation) {
      return {
        lat: userLocation.lat,
        lng: userLocation.lng,
      };
    }

    if (restaurants.length > 0) {
      const validRestaurants = restaurants.filter(
        (r) => r.storeLat !== 0 && r.storeLong !== 0 &&
          (r.status === "Active" || r.Status === "Active") &&
          r.isHighway === true
      );

      if (validRestaurants.length > 0) {
        const avgLat =
          validRestaurants.reduce((sum, r) => sum + r.storeLat, 0) /
          validRestaurants.length;
        const avgLng =
          validRestaurants.reduce((sum, r) => sum + r.storeLong, 0) /
          validRestaurants.length;

        return {
          lat: avgLat,
          lng: avgLng,
        };
      }
    }

    // Default to Hyderabad if no data available
    return {
      lat: 17.385044,
      lng: 78.486671,
    };
  }, [userLocation, restaurants]);

  // Create restaurant marker icon
  const createRestaurantMarkerIcon = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" fill="%23FCD34D" stroke="white" stroke-width="2"/>
      <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill="white" transform="translate(2, 2) scale(0.8)"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // Create user location icon
  const createUserLocationIcon = () => {
    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
      scale: 8,
      fillColor: "#3B82F6",
      fillOpacity: 0.9,
      strokeColor: "#FFFFFF",
      strokeWeight: 3,
    };
  };

  if (typeof window.google === "undefined" && !window.google.maps) {
    return <AnimatedLoader />;
  }
  return (
    <div className="main-page" style={{ minHeight: "100vh" }}>
      {/* Search Bar Section */}
      <div
        style={{
          padding: "16px",
          paddingBottom: "8px",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            padding: "12px 16px",
            border: isSearchFocused
              ? "2px solid rgba(252, 211, 77, 0.6)"
              : "2px solid rgba(255, 255, 255, 0.2)",
            transition: "all 0.3s ease",
          }}
        >
          <Search
            size={20}
            color={isSearchFocused ? "#FCD34D" : "rgba(255, 255, 255, 0.6)"}
            style={{ marginRight: "12px", flexShrink: 0 }}
          />
          <input
            type="text"
            placeholder="Search restaurants..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={() => {
              setTimeout(() => {
                setIsSearchFocused(false);
                setShowDropdown(false);
              }, 200);
            }}
            style={{
              flex: 1,
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              color: "white",
              fontSize: "16px",
              fontWeight: "500",
            }}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                border: "none",
                borderRadius: "50%",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                marginLeft: "8px",
              }}
            >
              <X size={16} color="white" />
            </button>
          )}
        </div>

        {/* Search Dropdown */}
        {showDropdown && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% - 8px)",
              left: "16px",
              right: "16px",
              backgroundColor: "rgba(30, 30, 30, 0.98)",
              backdropFilter: "blur(20px)",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              maxHeight: "400px",
              overflowY: "auto",
              zIndex: 1000,
            }}
          >
            {searchResults.length > 0 ? (
              <div>
                {searchResults.map((restaurant, index) => (
                  <div
                    key={restaurant.dbID}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleDropdownItemClick(restaurant.dbID);
                    }}
                    style={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      borderBottom:
                        index < searchResults.length - 1
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "none",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(252, 211, 77, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={restaurant.image}
                          alt={restaurant.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            color: "white",
                            fontSize: "14px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {restaurant.name}
                        </div>
                        <div
                          style={{
                            color: "rgba(255, 255, 255, 0.6)",
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {restaurant.storeType}
                        </div>
                      </div>
                      {restaurant.distance && (
                        <div
                          style={{
                            color: "#FCD34D",
                            fontSize: "12px",
                            fontWeight: "600",
                            flexShrink: 0,
                          }}
                        >
                          {restaurant.distance.toFixed(1)} km
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: "24px 16px",
                  textAlign: "center",
                  color: "rgba(255, 255, 255, 0.6)",
                }}
              >
                <Search
                  size={32}
                  style={{ margin: "0 auto 12px", opacity: 0.5 }}
                />
                <p style={{ fontSize: "14px", margin: 0 }}>
                  No restaurants found for "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Carousel Section */}
      <MainPageCarousel key={restID} restID={restID} />

      {/* Map Preview Section */}
      <div style={{ padding: "16px" }} className="!pt-0 !pb-8">
        <div
          onClick={handleMapClick}
          style={{
            position: "relative",
            borderRadius: "16px",
            overflow: "hidden",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            height: "200px",
          }}
        >
          {/* Enable Location Button */}
          {isUsingDefaultLocation && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                requestLocation();
              }}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 10,
                backgroundColor: "rgba(252, 211, 77, 0.95)",
                color: "#1a1a1a",
                border: "none",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "12px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(252, 211, 77, 1)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(252, 211, 77, 0.95)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <Navigation size={14} />
              Enable Location
            </button>
          )}

          {/* Map Preview */}
          {typeof window.google !== "undefined" && window.google.maps ? (
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "100%",
                borderRadius: "16px",
              }}
              center={mapCenter}
              zoom={userLocation ? 13 : 12}
              options={{
                disableDefaultUI: true,
                gestureHandling: "none",
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                draggable: false,
                mapId:
                  process.env.REACT_APP_GOOGLE_MAP_ID ||
                  "c20dacceaeabfb2c718d014c",
              }}
            >
              {/* User location marker */}
              {userLocation && (
                <>
                  <Marker
                    position={{
                      lat: userLocation.lat,
                      lng: userLocation.lng,
                    }}
                    icon={createUserLocationIcon()}
                  />
                  <Marker
                    position={{
                      lat: userLocation.lat,
                      lng: userLocation.lng,
                    }}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                      scale: 15,
                      fillColor: "#3B82F6",
                      fillOpacity: 0.2,
                      strokeColor: "#3B82F6",
                      strokeWeight: 1,
                    }}
                  />
                </>
              )}

              {/* Restaurant markers - show only nearby ones */}
              {nearestStores.slice(0, 10).map((restaurant) => (
                <Marker
                  key={restaurant.dbID}
                  position={{
                    lat: restaurant.storeLat,
                    lng: restaurant.storeLong,
                  }}
                  icon={createRestaurantMarkerIcon()}
                />
              ))}
            </GoogleMap>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ textAlign: "center", color: "white" }}>
                <MapPin size={48} />
                <AnimatedLoader />
              </div>
            </div>
          )}

          {/* Overlay with gradient and text */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 60%, transparent 100%)",
              padding: "12px 16px",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "700",
                    margin: 0,
                    lineHeight: "1.4",
                  }}
                >
                  Tap to view Nearby Restaurants
                </h3>
              </div>
              <div
                style={{
                  backgroundColor: "rgba(252, 211, 77, 0.9)",
                  borderRadius: "50%",
                  padding: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                }}
              >
                <Navigation size={18} color="#1a1a1a" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nearest Stores Section */}
      {nearestStores.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <h2
              style={{
                color: "white",
                fontSize: "20px",
                fontWeight: "700",
                margin: 0,
              }}
            >
              Popular Restaurants Nearby
            </h2>
            {isCalculatingDistances && (
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(252, 211, 77, 0.8)",
                  fontWeight: "500",
                }}
              >
                Calculating distances...
              </div>
            )}
          </div>

          {/* Debug info - Remove this after testing */}
          {/* <div
            style={{
              background: "rgba(255,255,255,0.1)",
              padding: "10px",
              marginBottom: "10px",
              borderRadius: "8px",
              fontSize: "12px",
              color: "white",
            }}
          >
            <div>Total restaurants: {nearestStores.length}</div>
            <div>Distances fetched: {distancesFetched}</div>
            <div>
              First restaurant distance:{" "}
              {nearestStores[0]?.distance?.toFixed(2)} km
            </div>
            <div>
              Has distanceText: {nearestStores[0]?.distanceText || "No"}
            </div>
          </div> */}

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {nearestStores.map((store) => (
              <RestaurantCard
                key={`${store.dbID}-${store.distance}`}
                restaurant={store}
                onClick={() => handleStoreClick(store.dbID, store.storeType)}
              />
            ))}
            {nearestStores.length >= visibleCount && (
              <button
                onClick={() => setVisibleCount(prev => prev + 5)}
                disabled={isCalculatingDistances}
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "rgba(252, 211, 77, 0.1)",
                  color: "#FCD34D",
                  border: "1px solid rgba(252, 211, 77, 0.3)",
                  borderRadius: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  width: "100%",
                  opacity: isCalculatingDistances ? 0.5 : 1,
                  transition: "all 0.3s ease"
                }}
              >
                {isCalculatingDistances ? "Calculating..." : "View More Nearby Restaurants"}
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default MapAndStores;
